import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Calendar, MapPin, Clock, Building2, AlertCircle, CheckCircle2,
  Loader2, XCircle, Info, Mail, Hourglass, ShieldAlert, TrendingUp
} from 'lucide-react';

interface MyRegistrationData {
  registration: {
    candidate_id: string;
    full_name: string;
    email: string | null;
    mobile_number: string;
    status: string;
    approval_status: string;
    pending_since: string | null;
    approved_at: string | null;
    rejected_at: string | null;
    rejection_reason_code: string | null;
    rejection_reason_text: string | null;
    revocation_reason: string | null;
    overbooked: boolean;
    waitlist_position: number | null;
    waitlist_withdraw_token: string | null;
    self_cancelled_at: string | null;
    registered_at: string | null;
    payment_status: string | null;
    payment_amount_nok: number;
    can_self_cancel: boolean;
    self_cancel_blocked_reason: string | null;
  };
  instance: {
    id: string;
    name: string;
    start_date: string | null;
    end_date: string | null;
    exam_scheduled_at: string | null;
    location: string | null;
    self_cancel_deadline_days: number;
    approval_mode: 'auto' | 'hybrid' | 'manual';
    host_organization: string;
    host_email: string | null;
  };
}

const STATUS_LABELS: Record<string, string> = {
  registered: 'Påmeldt',
  waitlist: 'Venteliste',
  self_cancelled: 'Avmeldt',
  invited: 'Invitert til eksamen',
  exam_started: 'Eksamen pågår',
  exam_completed: 'Eksamen fullført',
  no_show_pending_reschedule: 'Ikke fremmøtt — venter på ny dato',
  graded: 'Vurdert',
  claimed: 'Attest hentet',
  certificate_issued: 'Attest utstedt'
};

const STATUS_COLORS: Record<string, string> = {
  registered: 'bg-green-100 text-green-800',
  waitlist: 'bg-yellow-100 text-yellow-800',
  self_cancelled: 'bg-gray-100 text-gray-800',
  invited: 'bg-kf-blue-100 text-kf-blue-800',
  exam_started: 'bg-kf-blue-100 text-kf-blue-800',
  exam_completed: 'bg-purple-100 text-purple-800',
  no_show_pending_reschedule: 'bg-kf-orange-100 text-kf-orange-800',
  graded: 'bg-purple-100 text-purple-800',
  claimed: 'bg-green-100 text-green-800',
  certificate_issued: 'bg-green-100 text-green-800'
};

export function MyRegistrationPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<MyRegistrationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelResult, setCancelResult] = useState<string | null>(null);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/public/my-registration/${token}`);
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || json.error || 'Fant ikke påmeldingen');
      }
      setData(json);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunne ikke laste påmeldingen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleCancel = async () => {
    if (!token) return;
    setCancelling(true);
    try {
      const res = await fetch(`/api/public/my-registration/${token}/cancel`, {
        method: 'POST'
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || json.error || 'Avmelding mislyktes');
      }
      setCancelResult(json.message);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Avmelding mislyktes');
    } finally {
      setCancelling(false);
      setConfirmingCancel(false);
    }
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString('no-NO', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const formatDateTime = (iso: string | null) => {
    if (!iso) return null;
    return new Date(iso).toLocaleString('no-NO', {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-kf-blue-700" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg border border-kf-error-200 p-6 text-center">
          <AlertCircle className="w-12 h-12 text-kf-error-600 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Fant ikke påmeldingen</h1>
          <p className="text-gray-600">{error || 'Lenken er ugyldig.'}</p>
        </div>
      </div>
    );
  }

  const { registration, instance } = data;
  const statusColor = STATUS_COLORS[registration.status] || 'bg-gray-100 text-gray-800';
  const statusLabel = STATUS_LABELS[registration.status] || registration.status;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Header — Kompetanseforbundet brand */}
          <div className="bg-gradient-to-r from-kf-blue-800 to-kf-blue-950 px-6 py-5 text-white border-b-4 border-kf-orange-600">
            <div className="text-sm text-kf-blue-100 mb-1">Min påmelding</div>
            <h1 className="text-2xl font-bold">{instance.name}</h1>
            <p className="text-kf-blue-100 mt-1 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {instance.host_organization}
            </p>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6">
            {/* v6.6.1: Approval-status er primær visning */}
            <ApprovalStatusPanel registration={registration} instance={instance} />

            {/* Legacy status */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">Prøve-status:</span>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusColor}`}>
                {statusLabel}
              </span>
              {registration.status === 'waitlist' && registration.waitlist_position && (
                <span className="text-sm text-gray-600">
                  (Posisjon: #{registration.waitlist_position})
                </span>
              )}
            </div>

            {cancelResult && (
              <div className="bg-kf-success-50 border border-kf-success-200 rounded p-4 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-kf-success-600 mt-0.5" />
                <div className="text-kf-blue-950">{cancelResult}</div>
              </div>
            )}

            {/* Kandidat-info */}
            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-gray-500">Navn</div>
                <div className="font-medium">{registration.full_name}</div>
              </div>
              <div>
                <div className="text-gray-500">Mobil</div>
                <div className="font-medium">{registration.mobile_number}</div>
              </div>
              {registration.email && (
                <div>
                  <div className="text-gray-500">E-post</div>
                  <div className="font-medium">{registration.email}</div>
                </div>
              )}
              {registration.registered_at && (
                <div>
                  <div className="text-gray-500">Påmeldt</div>
                  <div className="font-medium">{formatDate(registration.registered_at)}</div>
                </div>
              )}
            </div>

            {/* Kurs-detaljer */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {instance.start_date && (
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-kf-blue-700 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Kursstart</div>
                    <div className="font-medium">{formatDate(instance.start_date)}</div>
                  </div>
                </div>
              )}
              {instance.exam_scheduled_at && (
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-kf-orange-600 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Eksamen</div>
                    <div className="font-medium">{formatDateTime(instance.exam_scheduled_at)}</div>
                  </div>
                </div>
              )}
              {instance.location && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-600 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Sted</div>
                    <div className="font-medium">{instance.location}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Betalingsstatus */}
            {registration.payment_status && registration.payment_status !== 'not_required' && (
              <div className="bg-kf-orange-50 border border-kf-orange-200 rounded p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-kf-orange-700 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-kf-blue-950">
                      Påmeldingsgebyr: {registration.payment_amount_nok.toLocaleString('no-NO')} kr
                    </div>
                    <div className="text-sm text-gray-700 mt-1">
                      Status: <strong>{registration.payment_status === 'pending' ? 'Avventer betaling' :
                        registration.payment_status === 'paid' ? 'Betalt' :
                        registration.payment_status === 'failed' ? 'Mislyktes' :
                        registration.payment_status}</strong>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Selvavmelding */}
            {registration.status !== 'self_cancelled' && (
              <div className="border-t border-gray-200 pt-6">
                <h2 className="font-semibold text-gray-900 mb-2">Avmelding</h2>
                {registration.can_self_cancel ? (
                  <>
                    <p className="text-sm text-gray-700 mb-3">
                      Du kan melde deg av selv frem til {instance.self_cancel_deadline_days} dager før kursstart.
                      {registration.payment_amount_nok > 0 && (
                        <strong> Påmeldingsgebyret refunderes ikke.</strong>
                      )}
                    </p>
                    {!confirmingCancel ? (
                      <button
                        onClick={() => setConfirmingCancel(true)}
                        className="px-4 py-2 bg-kf-error-600 hover:bg-kf-error-700 text-white rounded font-medium transition-colors"
                      >
                        Meld meg av
                      </button>
                    ) : (
                      <div className="bg-kf-error-50 border border-kf-error-300 rounded p-4 space-y-3">
                        <p className="font-medium text-kf-error-800">
                          Er du sikker på at du vil melde deg av?
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={handleCancel}
                            disabled={cancelling}
                            className="px-4 py-2 bg-kf-error-600 hover:bg-kf-error-700 disabled:bg-gray-400 text-white rounded font-medium transition-colors"
                          >
                            {cancelling ? 'Avmelder...' : 'Ja, meld meg av'}
                          </button>
                          <button
                            onClick={() => setConfirmingCancel(false)}
                            className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded font-medium"
                          >
                            Avbryt
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded p-4">
                    <div className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-gray-500 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {registration.self_cancel_blocked_reason === 'deadline_passed'
                            ? 'Selvavmelding er ikke lenger mulig'
                            : 'Selvavmelding er ikke mulig'}
                        </div>
                        <p className="text-sm text-gray-700 mt-1">
                          {registration.self_cancel_blocked_reason === 'deadline_passed'
                            ? `Det er færre enn ${instance.self_cancel_deadline_days} dager igjen til kursstart. Kontakt kursarrangøren direkte.`
                            : `Kontakt kursarrangøren direkte for å avmelde.`}
                        </p>
                        {instance.host_email && (
                          <a
                            href={`mailto:${instance.host_email}`}
                            className="inline-flex items-center gap-1 text-kf-blue-700 hover:underline text-sm mt-2 font-medium"
                          >
                            <Mail className="w-4 h-4" /> {instance.host_email}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="text-center mt-6 text-xs text-gray-500">
          En tjeneste fra <strong className="text-kf-blue-950">Kompetanseforbundet</strong>
        </div>
      </div>
    </div>
  );
}

/**
 * ApprovalStatusPanel — viser tydelig status på godkjenning
 * (pending / approved / rejected / revoked / waitlist).
 */
function ApprovalStatusPanel({ registration, instance }: {
  registration: MyRegistrationData['registration'];
  instance: MyRegistrationData['instance'];
}) {
  const withdrawUrl = registration.waitlist_withdraw_token
    ? `/venteliste/trekk-meg/${registration.waitlist_withdraw_token}`
    : null;

  // Pending approval
  if (registration.approval_status === 'pending_approval') {
    const daysWaiting = registration.pending_since
      ? Math.floor((Date.now() - new Date(registration.pending_since).getTime()) / (1000 * 60 * 60 * 24))
      : 0;
    return (
      <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-5">
        <div className="flex items-start gap-3">
          <Hourglass className="w-8 h-8 text-kf-blue-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="text-lg font-bold text-kf-blue-950 mb-1">Venter på bekreftelse fra arrangør</h2>
            <p className="text-sm text-kf-blue-900 mb-2">
              Kursarrangør <strong>{instance.host_organization}</strong> vurderer nå påmeldingen din.
              Du får beskjed så snart plassen er bekreftet — vanligvis innen få dager.
            </p>
            <p className="text-xs text-kf-blue-800">
              Har ventet i {daysWaiting} {daysWaiting === 1 ? 'dag' : 'dager'}. Ta gjerne kontakt med
              arrangøren hvis du har spørsmål.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Rejected
  if (registration.approval_status === 'rejected' || registration.approval_status === 'auto_rejected') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-5">
        <div className="flex items-start gap-3">
          <XCircle className="w-8 h-8 text-red-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="text-lg font-bold text-red-900 mb-1">
              Påmeldingen ble ikke godkjent
            </h2>
            <p className="text-sm text-red-800 mb-2">
              {registration.approval_status === 'auto_rejected'
                ? 'Påmeldingen ble automatisk avvist etter at fristen for arrangørens bekreftelse gikk ut.'
                : 'Kursarrangør har vurdert påmeldingen din, men kan dessverre ikke gi deg plass.'}
            </p>
            {registration.rejection_reason_text && (
              <div className="bg-white/60 rounded p-3 text-sm text-red-900 mt-2">
                <strong>Begrunnelse fra arrangør:</strong>
                <div className="mt-1 whitespace-pre-line">{registration.rejection_reason_text}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Registration revoked
  if (registration.approval_status === 'registration_revoked') {
    return (
      <div className="bg-red-50 border border-red-300 rounded-lg p-5">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-8 h-8 text-red-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="text-lg font-bold text-red-900 mb-1">Plassen din er trukket tilbake</h2>
            <p className="text-sm text-red-800 mb-2">
              Kursarrangør <strong>{instance.host_organization}</strong> har trukket tilbake plassen din.
            </p>
            {registration.revocation_reason && (
              <div className="bg-white/60 rounded p-3 text-sm text-red-900">
                <strong>Begrunnelse:</strong>
                <div className="mt-1 whitespace-pre-line">{registration.revocation_reason}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Waitlist (approved men på venteliste)
  if (registration.status === 'waitlist') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
        <div className="flex items-start gap-3">
          <TrendingUp className="w-8 h-8 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="text-lg font-bold text-amber-900 mb-1">
              Du står på venteliste{registration.waitlist_position ? ` (posisjon #${registration.waitlist_position})` : ''}
            </h2>
            <p className="text-sm text-amber-900 mb-3">
              Du får beskjed på e-post og SMS hvis du får tildelt plass.
            </p>
            {withdrawUrl && (
              <a
                href={withdrawUrl}
                className="text-sm text-amber-800 hover:text-amber-950 underline"
              >
                Trekk meg fra ventelisten →
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Approved
  if (registration.approval_status === 'approved') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-5">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-8 h-8 text-green-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="text-lg font-bold text-green-900 mb-1">
              Plassen din er bekreftet
              {registration.overbooked && <span className="ml-2 text-xs font-normal text-amber-700">(ekstra tildeling)</span>}
            </h2>
            <p className="text-sm text-green-800">
              Du er registrert deltaker på <strong>{instance.name}</strong>.
              {registration.approved_at && (
                <> Bekreftet {new Date(registration.approved_at).toLocaleDateString('no-NO')}.</>
              )}
            </p>
            <p className="text-xs text-green-700 mt-2">
              Merk: Kursarrangør kan i unntakstilfeller måtte gjøre endringer i påmeldingen.
              Vi tar i så fall kontakt med deg så snart som mulig.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
